import type { FormField } from '@backstage/plugin-scaffolder-react/alpha';
import { createFormField } from '@backstage/plugin-scaffolder-react/alpha';
import type { OptionSource } from '@ns/plugin-picker-toolbox-contracts';

import { pickerFieldSchema } from './choice-field-schema';
import { ChoiceFormField } from './choice-form-field';

type StaticChoiceSource<TItem, TValue> = {
  /** The concrete adapter (local mock or remote transport). */
  source: OptionSource<TItem, TValue>;
  useSource?: never;
};

type HookChoiceSource<TItem, TValue> = {
  source?: never;
  /** The per-instance source hook, called in the bound Field render. */
  useSource: () => OptionSource<TItem, TValue>;
};

/**
 * The options to register a picker as a scaffolder form field. Binds a name (the
 * `ui:field` value a template uses), exactly one defined source construction
 * form, and the adapter-specific base config. With
 * `exactOptionalPropertyTypes=false`, an explicit `undefined` opposite property
 * is equivalent to omission.
 */
export type CreateChoiceFieldOptions<TItem, TValue, TConfig> = {
  /** The adapter-specific base config (e.g. a resource-type descriptor). */
  baseConfig: TConfig;
  /** The `ui:field` name a template author references. */
  name: string;
} & (StaticChoiceSource<TItem, TValue> | HookChoiceSource<TItem, TValue>);

/**
 * Registers a picker as a scaffolder form field via the new frontend system's
 * `createFormField`. This deliberately avoids the legacy
 * `createScaffolderFieldExtension`: the new form lets a portal admin toggle
 * pickers per environment via `app.extensions` (02-ui-layer §6).
 *
 * The returned {@link FormField} is passed to a `FormFieldBlueprint` in the
 * consuming frontend plugin to make the picker installable.
 * This module owns the binding of a concrete OptionSource to the generic Field
 * component. A static source is normalized at factory time, while a hook-owned
 * source is resolved in the bound component. Neither form introduces
 * cloud-provider knowledge.
 */
export function createChoiceFormField<TItem, TValue, TConfig>(
  options: CreateChoiceFieldOptions<TItem, TValue, TConfig>,
): FormField {
  const { baseConfig, name } = options;
  const useSource =
    options.useSource ??
    (() => {
      const staticSource = options.source;
      return () => staticSource;
    })();

  return createFormField({
    component: ChoiceFormField(useSource, baseConfig),
    name,
    schema: pickerFieldSchema,
  });
}
